import 'smart-webcomponents-react/source/styles/smart.default.css';
import { Scheduler } from 'smart-webcomponents-react/scheduler';
import { Box, GlobalStyles } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TimetableCalendar as TimetableCalendarType,
  TimetableCalendarItem,
  TimetableStatus,
} from 'types/timetable.types';
import useFnColorByStatus from './useColorByStatus';

// https://www.htmlelements.com/react/demos/scheduler/grouping-horizontal-basic/

function TimetableCalendar({ data }: { data: TimetableCalendarType }) {
  const { t } = useTranslation('_common');

  const priorities = React.useMemo(() => {
    const daysOfWeek = Object.entries(data).filter(
      (item) => item[1] && item[0] !== '__typename',
    ) as [keyof TimetableCalendarType, TimetableCalendarItem[]][];
    return daysOfWeek.map((item, index) => ({
      label: t(item[0]),
      id: index + 1,
    }));
  }, [data, t]);

  const resources = React.useMemo(() => {
    return [
      {
        label: 'Priority',
        value: 'priorityId',
        dataSource: priorities,
      },
    ];
  }, [priorities]);

  const getStatusDesc = React.useCallback(
    (status: TimetableStatus) => {
      if (status === 'A') {
        return t('armed');
      }
      if (status === 'D') {
        return t('disarmed');
      }
      return t('transition');
    },
    [t],
  );

  const getBgColorByStatus = useFnColorByStatus();

  const dataComputed = React.useMemo(
    () =>
      (
        Object.entries(data).filter((item) => item[1] && item[0] !== '__typename') as [
          keyof TimetableCalendarType,
          TimetableCalendarItem[],
        ][]
      )
        .map((item) =>
          item[1].map((items) => ({
            priorityId: priorities.find((priority) => priority.label === t(item[0]))?.id,
            label: getStatusDesc(items.status),
            dateStart: dayjs(items.from, 'HH:mm').toDate(),
            dateEnd: dayjs(items.to, 'HH:mm').toDate(),
            backgroundColor: getBgColorByStatus(items.status),
          })),
        )
        .flat(),
    [data, getBgColorByStatus, getStatusDesc, priorities, t],
  );

  return (
    <>
      <Box flex="1 1 auto" overflow="auto" sx={{ p: 3 }}>
        <GlobalStyles
          styles={{
            '.smart-scheduler-event-button, .smart-button, .smart-repeat-button': {
              display: 'none!important',
            },
            '.smart-scheduler-header': {
              border: 'none!important',
            },
            body: {
              '--smart-scheduler-header-height': '0px',
              '--smart-scheduler-header-padding': '0px',
              '--smart-scheduler-timeline-header-all-day-cells-size': '0px',
            },
          }}
        />
        <Scheduler
          id="scheduler"
          dataSource={dataComputed}
          view={'day'}
          views="day"
          groups={['priorityId']}
          resources={resources}
          disableDrag
          disableDrop
          disableResize
          disableSelection
          disableWindowEditor
          disableContextMenu
          disableEventMenu
          disableViewMenu
          disableDateMenu
        />
      </Box>
    </>
  );
}

export default TimetableCalendar;
